/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { useEffect } from "react";

// Analytics and tracking configuration
const ANALYTICS_CONFIG = {
  // Google Analytics 4
  GA4_TRACKING_ID: "G-XXXXXXXXXX", // Replace with your GA4 tracking ID

  // Google Tag Manager
  GTM_ID: "GTM-XXXXXXX", // Replace with your GTM container ID

  // Facebook Pixel
  FB_PIXEL_ID: "XXXXXXXXXXXXXX", // Replace with your Facebook Pixel ID

  // Twitter Analytics
  TWITTER_PIXEL_ID: "XXXXX", // Replace with your Twitter Pixel ID

  // Hot jar or other heat mapping tools
  HOTJAR_ID: "XXXXXXX", // Replace with your Hotjar site ID

  // Custom events tracking
  ENABLE_CUSTOM_EVENTS: true,

  // Debug mode (set to false in production)
  DEBUG_MODE: process.env.NODE_ENV === "development",
};

/**
 * Initialize Google Analytics 4
 */
const initializeGA4 = () => {
  if (!ANALYTICS_CONFIG.GA4_TRACKING_ID || ANALYTICS_CONFIG.GA4_TRACKING_ID === "G-XXXXXXXXXX") {
    console.log("🔍 SEO: GA4 tracking ID not configured");
    return;
  }

  // Load gtag script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ANALYTICS_CONFIG.GA4_TRACKING_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", ANALYTICS_CONFIG.GA4_TRACKING_ID, {
    page_title: document.title,
    page_location: window.location.href,
    send_page_view: true,
    // Enhanced ecommerce for NFT tracking
    custom_parameters: {
      content_group1: "NFT Collection",
      content_group2: "Sailing Ships",
      user_engagement: true,
    },
  });

  console.log("📊 SEO: Google Analytics 4 initialized");
};

/**
 * Initialize Google Tag Manager
 */
const initializeGTM = () => {
  if (!ANALYTICS_CONFIG.GTM_ID || ANALYTICS_CONFIG.GTM_ID === "GTM-XXXXXXX") {
    console.log("🔍 SEO: GTM ID not configured");
    return;
  }

  // GTM script
  /* eslint-disable */
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l !== "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", ANALYTICS_CONFIG.GTM_ID);
  /* eslint-enable */

  // GTM noscript fallback
  const noscript = document.createElement("noscript");
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${ANALYTICS_CONFIG.GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.appendChild(noscript);

  console.log("📊 SEO: Google Tag Manager initialized");
};

/**
 * Initialize Facebook Pixel
 */
const initializeFacebookPixel = () => {
  if (!ANALYTICS_CONFIG.FB_PIXEL_ID || ANALYTICS_CONFIG.FB_PIXEL_ID === "XXXXXXXXXXXXXX") {
    console.log("🔍 SEO: Facebook Pixel ID not configured");
    return;
  }

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  window.fbq("init", ANALYTICS_CONFIG.FB_PIXEL_ID);
  window.fbq("track", "PageView");

  // Track NFT-specific events
  window.fbq("track", "ViewContent", {
    content_type: "nft_collection",
    content_name: "Sailing Ships NFT",
    content_category: "Digital Art",
  });

  console.log("📊 SEO: Facebook Pixel initialized");
};

/**
 * Initialize Hotjar
 */
const initializeHotjar = () => {
  if (!ANALYTICS_CONFIG.HOTJAR_ID || ANALYTICS_CONFIG.HOTJAR_ID === "XXXXXXX") {
    console.log("🔍 SEO: Hotjar ID not configured");
    return;
  }

  /* eslint-disable */
  (function (h, o, t, j, a, r) {
    h.hj =
      h.hj ||
      function () {
        (h.hj.q = h.hj.q || []).push(arguments);
      };
    h._hjSettings = { hjid: ANALYTICS_CONFIG.HOTJAR_ID, hjsv: 6 };
    a = o.getElementsByTagName("head")[0];
    r = o.createElement("script");
    r.async = 1;
    r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
    a.appendChild(r);
  })(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");
  /* eslint-enable */

  console.log("📊 SEO: Hotjar initialized");
};

/**
 * Track custom events for NFT interactions
 */
export const trackNFTEvent = (eventName, eventData = {}) => {
  if (!ANALYTICS_CONFIG.ENABLE_CUSTOM_EVENTS) return;

  const eventPayload = {
    event_category: "NFT Interaction",
    event_label: "Sailing Ships Collection",
    ...eventData,
  };

  // Google Analytics 4
  if (window.gtag) {
    window.gtag("event", eventName, eventPayload);
  }

  // Facebook Pixel
  if (window.fbq) {
    window.fbq("trackCustom", eventName, eventPayload);
  }

  // Custom tracking
  if (ANALYTICS_CONFIG.DEBUG_MODE) {
    console.log("📊 SEO: Custom event tracked:", eventName, eventPayload);
  }
};

/**
 * Track page views for SPA navigation
 */
export const trackPageView = (pageName, additionalData = {}) => {
  const pageData = {
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    content_group1: "NFT Collection",
    content_group2: pageName,
    ...additionalData,
  };

  // Google Analytics 4
  if (window.gtag) {
    window.gtag("config", ANALYTICS_CONFIG.GA4_TRACKING_ID, pageData);
  }

  // Facebook Pixel
  if (window.fbq) {
    window.fbq("track", "PageView", {
      content_name: pageName,
      content_category: "NFT Collection",
    });
  }

  if (ANALYTICS_CONFIG.DEBUG_MODE) {
    console.log("📊 SEO: Page view tracked:", pageName, pageData);
  }
};

/**
 * Track NFT collection interactions
 */
export const trackCollectionInteraction = (action, nftId = null, additionalData = {}) => {
  const eventData = {
    event_category: "Collection",
    event_label: nftId || "General",
    value: 1,
    ...additionalData,
  };

  trackNFTEvent(`collection_${action}`, eventData);
};

/**
 * Track user engagement with interactive elements
 */
export const trackInteractiveEngagement = (element, duration = null) => {
  const eventData = {
    event_category: "Interactive Elements",
    event_label: element,
    value: duration || 1,
  };

  trackNFTEvent("interactive_engagement", eventData);
};

/**
 * Track performance metrics
 */
export const trackPerformanceMetrics = () => {
  if (!window.performance) return;

  const navigation = performance.getEntriesByType("navigation")[0];
  const paint = performance.getEntriesByType("paint");

  const metrics = {
    page_load_time: Math.round(navigation.loadEventEnd - navigation.loadEventStart),
    dom_content_loaded: Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart),
    first_paint: paint.find((p) => p.name === "first-paint")?.startTime || 0,
    first_contentful_paint: paint.find((p) => p.name === "first-contentful-paint")?.startTime || 0,
  };

  if (window.gtag) {
    window.gtag("event", "performance_metrics", {
      event_category: "Site Performance",
      custom_parameters: metrics,
    });
  }

  if (ANALYTICS_CONFIG.DEBUG_MODE) {
    console.log("⚡ SEO: Performance metrics tracked:", metrics);
  }
};

/**
 * SEOAnalytics Component - Initialize all tracking scripts
 */
const SEOAnalytics = () => {
  useEffect(() => {
    // Initialize all analytics services
    initializeGA4();
    initializeGTM();
    initializeFacebookPixel();
    initializeHotjar();

    // Track initial page load performance
    setTimeout(() => {
      trackPerformanceMetrics();
    }, 2000);

    // Track user engagement
    const handleUserEngagement = () => {
      trackNFTEvent("user_engagement", {
        event_category: "User Behavior",
        engagement_time: Date.now(),
      });
    };

    // Track scroll depth
    let maxScrollDepth = 0;
    const handleScroll = () => {
      const scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;
        if (maxScrollDepth % 25 === 0) {
          // Track at 25%, 50%, 75%, 100%
          trackNFTEvent("scroll_depth", {
            event_category: "User Behavior",
            event_label: `${maxScrollDepth}%`,
            value: maxScrollDepth,
          });
        }
      }
    };

    // Track time on page
    const startTime = Date.now();
    const trackTimeOnPage = () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      trackNFTEvent("time_on_page", {
        event_category: "User Behavior",
        value: timeSpent,
      });
    };

    // Add event listeners
    window.addEventListener("beforeunload", trackTimeOnPage);
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Track engagement after 30 seconds
    const engagementTimer = setTimeout(handleUserEngagement, 30000);

    return () => {
      window.removeEventListener("beforeunload", trackTimeOnPage);
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(engagementTimer);
    };
  }, []);

  return null; // This component doesn't render anything visible
};

export default SEOAnalytics;
